import BodyText from "components/Typography/BodyText"

import React, { memo, useMemo } from "react"
import styled from "styled-components"
import clsx from "clsx"
import IconInformativeError from "../icons/IconInformativeError"

const TextAreaSection = styled.div`
  position: relative;
`

const StyledTextArea = styled.textarea`
  display: block;
  width: 340px;
  height: 48px;

  padding: 24px 16px;

  font-size: 14px;
  line-height: 17px;

  resize: none;

  :focus,
  &.shrink {
    outline: none;
  }

  background-color: transparent;
  color: ${(props) => props.theme.palette.primary_one};

  border: 1px solid ${(props) => props.theme.palette.primary_one};
  border-radius: 8px;

  &.error {
    border: 1px solid ${(props) => props.theme.palette.error};
    color: ${(props) => props.theme.palette.error};
  }

  &.fullWidth {
    width: 100%;
  }

  &.fullHeight {
    height: 100%;
  }

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    border-color: ${(props) => props.theme.palette.disabled};

    &::placeholder {
      color: ${(props) => props.theme.palette.disabled};
    }
  }

  ::placeholder {
    font-size: 14px;
    line-height: 18px;
    font-style: normal;
    font-weight: 400;
    color: ${(props) => props.theme.palette.primary_one};
  }

  &.isResizable {
    resize: vertical;
  }
`

const ErrorText = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) => props.theme.palette.error};
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 4px;
  height: 17px;

  &.unvisible {
    width: 0px;
    overflow: hidden;
  }
`

const LabelForm = styled.label`
  position: absolute;
  pointer-events: none;
  left: 15px;
  top: 14px;
  transition: 0.2s ease all;
  color: ${(props) => props.theme.palette.primary_one};
  ${StyledTextArea}:focus ~ &,
  ${StyledTextArea}:not(:placeholder-shown) ~ &,
  &.shrink {
    top: -8px;
    padding: 0 4px 0 4px;
    font-size: 12px;
    background-color: ${(props) => props.theme.palette.white};
  }

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
  }

  &.error {
    color: ${(props) => props.theme.palette.error};
  }
`

const Label = styled.div`
  font-size: 14px;
  line-height: 18px;

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    line-height: 19px;
  }

  &.error {
    color: ${(props) => props.theme.palette.error};
  }

  &.primary {
    color: ${(props) => props.theme.palette.primary_one};
  }
`

interface InputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  error?: string | boolean
  safeBottom?: boolean
  shrink?: boolean
  disabled?: boolean
  required?: boolean
  isResizable?: boolean
  iconError?: boolean
}

const TextArea: React.FC<InputProps> = (props: InputProps) => {
  const {
    className,
    error,
    id,
    label,
    onChange,
    onFocus,
    placeholder,
    safeBottom = false,
    shrink = false,
    disabled = false,
    isResizable = false,
    iconError = false,
    ...rest
  } = props

  const errorVisible = error ? "" : "unvisible"
  const showErrorSection = safeBottom || error

  const labelColor = useMemo(() => {
    if (disabled) return "gray-light"

    return error ? "error" : "primary-dark"
  }, [disabled, error])

  return (
    <TextAreaSection className={className}>
      <StyledTextArea
        onChange={onChange}
        onFocus={onFocus}
        className={clsx(`${className} ${clsx({ error: !!error })}`, { shrink }, { disabled }, { isResizable })}
        placeholder={placeholder || " "}
        disabled={disabled}
        {...rest}
      />
      <LabelForm htmlFor={id} className={clsx(className, { shrink })}>
        <BodyText size="small" weight="light" color={labelColor}>
          {label}
        </BodyText>
      </LabelForm>
      {showErrorSection && (
        <ErrorText id="text-area-error" className={clsx(className, errorVisible)}>
          {iconError && <IconInformativeError />}
          <Label color="error">{error}</Label>
        </ErrorText>
      )}
    </TextAreaSection>
  )
}

export default memo(TextArea)
